import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { canSeeAllHistory } from './HistoryLink'
import TutorialTour from './TutorialTour'
import { getEmergency } from '../lib/emergencyStore'
import { PAGE_MARK, shouldAutoStart, tourSteps } from '../lib/tutorialCore'
import { tutorialAutomated, tutorialStore } from '../lib/tutorialStore'

// Starts the tour (once per login, or "Restart tutorial") and notes the jobs that are done by doing them: opening a page, opening
// the app from the home screen, opening a vessel.
const isStandalone = () => (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true

export default function TutorialHost({ page, aiAllowed, onHome }) {
  const auth = useAuth()
  const { user, rawConfig, selectedVessel } = auth
  const [running, setRunning] = useState(false)
  const store = tutorialStore(user && user.id)
  // a link from an e-mail is on its way to its page: the tour waits for the next time (read while drawing: App clears it right after)
  const deepLink = useRef((() => { try { return !!sessionStorage.getItem('hc_deeplink') } catch (e) { return false } })())
  const home = useRef(onHome); home.current = onHome

  const start = useRef(() => {})
  start.current = () => {
    window.dispatchEvent(new CustomEvent('hc-assistant-close'))
    window.dispatchEvent(new CustomEvent('hc-guide-close'))
    home.current()
    setRunning(true)
  }
  useEffect(() => { const h = () => start.current(); window.addEventListener('hc-tour-start', h); return () => window.removeEventListener('hc-tour-start', h) }, [])
  useEffect(() => {
    const h = () => {
      window.dispatchEvent(new CustomEvent('hc-tour-menu', { detail: { open: false } }))
      window.dispatchEvent(new CustomEvent('hc-guide-close'))
      window.dispatchEvent(new CustomEvent('hc-checklist-reopen'))
      home.current()
    }
    window.addEventListener('hc-checklist-open', h)
    return () => window.removeEventListener('hc-checklist-open', h)
  }, [])

  useEffect(() => {
    if (!store) return
    let live = true, timer = null
    const check = () => {
      if (!live || !store.loaded) return
      if (!shouldAutoStart({ state: store.get(), automated: tutorialAutomated(), config: rawConfig, deepLink: deepLink.current, emergency: !!(getEmergency() && getEmergency().run) })) return
      if (navigator.onLine === false) return   // no signal: the login's first look at the app is not the moment
      timer = setTimeout(() => { if (live) start.current() }, 700)   // the page draws first
    }
    if (store.loaded) check(); else { const off = store.subscribe(() => { off(); check() }) }
    return () => { live = false; clearTimeout(timer) }
  }, [store]) // eslint-disable-line

  // jobs done by doing them
  useEffect(() => { if (store && PAGE_MARK[page]) store.mark(PAGE_MARK[page]) }, [store, page])
  useEffect(() => { if (store && isStandalone()) store.mark('install') }, [store])
  useEffect(() => { if (store && selectedVessel && auth.accountType === 'company') store.mark('open_vessel') }, [store, selectedVessel, auth.accountType])

  const steps = useMemo(() => (running ? tourSteps(auth, { aiAllowed, standalone: isStandalone(), canSeeAllHistory }) : null), [running]) // eslint-disable-line
  if (!running || !steps || !steps.length) return null
  return <TutorialTour steps={steps} onFinish={() => { setRunning(false); if (store) store.set({ tour_done: true }) }} />
}
